import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const MetaTagGeneratorTool = () => {
  const [data, setData] = useState({
    title: '',
    description: '',
    keywords: '',
    author: '',
    ogImage: '',
    twitterHandle: '',
    url: '',
  });

  const generateMeta = () => {
    const tags = [];
    
    if (data.title) {
      tags.push(`<title>${data.title}</title>`);
      tags.push(`<meta property="og:title" content="${data.title}" />`);
      tags.push(`<meta name="twitter:title" content="${data.title}" />`);
    }
    
    if (data.description) {
      tags.push(`<meta name="description" content="${data.description}" />`);
      tags.push(`<meta property="og:description" content="${data.description}" />`);
      tags.push(`<meta name="twitter:description" content="${data.description}" />`);
    }
    
    if (data.keywords) {
      tags.push(`<meta name="keywords" content="${data.keywords}" />`);
    }
    
    if (data.author) {
      tags.push(`<meta name="author" content="${data.author}" />`);
    }
    
    if (data.ogImage) {
      tags.push(`<meta property="og:image" content="${data.ogImage}" />`);
      tags.push(`<meta name="twitter:image" content="${data.ogImage}" />`);
      tags.push(`<meta name="twitter:card" content="summary_large_image" />`);
    }
    
    if (data.url) {
      tags.push(`<meta property="og:url" content="${data.url}" />`);
      tags.push(`<link rel="canonical" href="${data.url}" />`);
    }
    
    if (data.twitterHandle) {
      tags.push(`<meta name="twitter:site" content="@${data.twitterHandle.replace('@', '')}" />`);
    }
    
    tags.push(`<meta property="og:type" content="website" />`);
    tags.push(`<meta name="robots" content="index, follow" />`);
    
    return tags.join('\n');
  };

  const output = generateMeta();

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    toast.success('Meta tags copied!');
  };

  return (
    <div className="space-y-4" data-testid="meta-tag-generator-tool">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Meta Tag Generator</h3>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-sm text-muted-foreground mb-1 block">Page Title</label>
          <Input
            placeholder="My Awesome Page"
            value={data.title}
            onChange={(e) => setData({ ...data, title: e.target.value })}
            className="bg-background/50"
          />
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-1 block">Description</label>
          <Textarea
            placeholder="A brief description of your page..."
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
            className="bg-background/50 min-h-[80px]"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Keywords</label>
            <Input
              placeholder="keyword1, keyword2"
              value={data.keywords}
              onChange={(e) => setData({ ...data, keywords: e.target.value })}
              className="bg-background/50"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Author</label>
            <Input
              placeholder="John Doe"
              value={data.author}
              onChange={(e) => setData({ ...data, author: e.target.value })}
              className="bg-background/50"
            />
          </div>
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-1 block">Page URL</label>
          <Input
            placeholder="https://example.com/page"
            value={data.url}
            onChange={(e) => setData({ ...data, url: e.target.value })}
            className="bg-background/50"
          />
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-1 block">OG Image URL</label>
          <Input
            placeholder="https://example.com/image.jpg"
            value={data.ogImage}
            onChange={(e) => setData({ ...data, ogImage: e.target.value })}
            className="bg-background/50"
          />
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-1 block">Twitter Handle</label>
          <Input
            placeholder="@username"
            value={data.twitterHandle}
            onChange={(e) => setData({ ...data, twitterHandle: e.target.value })}
            className="bg-background/50"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">Generated Meta Tags</span>
          <Button variant="ghost" size="sm" onClick={copyOutput}>
            <Copy className="w-4 h-4 mr-1" /> Copy
          </Button>
        </div>
        <Textarea
          value={output}
          readOnly
          className="min-h-[200px] bg-background/50 font-mono text-xs"
          data-testid="output"
        />
      </div>
    </div>
  );
};

export default MetaTagGeneratorTool;
